import * as yup from "yup";
import { Formik } from "formik";
import { withRouter } from "react-router";
import { useAuthContextV2 } from "../../../auth-state";
import { LoginTemplate } from "../../template/login-template";
import apiClient from "../../../application/apis/tenant-management/tenant-management-client";
import { toast } from "react-toastify";
import { PasswordRegex } from "../../../application/domain/user/user-form-validation";
import { CloudGovApiError } from "../../../application/domain/api-error";
import {
  PrimaryButton,
  Spinner,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { reportUnexpectedError } from "../../../error-reporter";
import { useState } from "react";
import { UsersService } from "../../../application/service/smart-apply/users/users-service";

const defaultValidator = yup.object().shape({
  password: yup
    .string()
    .matches(PasswordRegex, {
      message:
        "パスワードは半角英数字記号8文字以上50文字以内で入力してください",
    })
    .required("パスワードを入力してください"),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref("password"), undefined], "パスワードが一致しません。")
    .required("パスワード（確認）は必須項目です。"),
});

export const ChangePasswordPage = withRouter(({ history }) => {
  const { syncAuth } = useAuthContextV2();
  const usersService = new UsersService();

  const [isPasswordPwned, setIsPasswordPwned] = useState<boolean>(false);

  // TODO: パスワード変更が求められている場合でなくとも、URL直打ちでこの画面に遷移してパスワードを変更できてしまっている。
  // テナントユーザー自身によるパスワード再設定の機能設計時に、この画面の位置づけを再考する。
  // ref. https://graffer.atlassian.net/browse/PRODUCTS-4746
  return (
    <LoginTemplate>
      <div className="section" style={{ margin: "-16px" }}>
        <div className="container has-text-left">
          <h1 className="title is-size-4">パスワードを変更してください</h1>
          {isPasswordPwned && (
            <WarningBanner
              message="推測されやすいパスワードです"
              description="入力されたパスワードは推測されやすいパスワードです。より安全なパスワードを設定することをおすすめします。"
            />
          )}

          <p
            className="help has-text-weight-normal"
            style={{ marginBottom: "10px", marginTop: "10px" }}
          >
            他のサイトで使いまわしていない、大文字、小文字、数字、記号を組み合わせたパスワードを設定してください
          </p>

          <Formik
            initialValues={{ password: "", passwordConfirm: "" }}
            validationSchema={defaultValidator}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setSubmitting(true);
                await apiClient.changePassword(values);
                await syncAuth();
                history.push("/");
              } catch (e) {
                let message = "パスワードの変更に失敗しました。";
                if (
                  CloudGovApiError.isErrorCode(
                    e,
                    "bad_request.same_as_current_password",
                  )
                ) {
                  message =
                    "現在設定されているパスワードと同じパスワードは設定できません。";
                }
                if (CloudGovApiError.tmpPasswordExpired(e)) {
                  message =
                    "パスワードの有効期限が切れています。管理者にパスワードのリセットを依頼してください。";
                }
                if (message === "パスワードの変更に失敗しました。") {
                  reportUnexpectedError(e);
                }
                toast.error(message);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              isValid,
              /* and other goodies */
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label className="label">更新後のパスワード</label>
                  <input
                    type="password"
                    name="password"
                    className="input"
                    onChange={async (e) => {
                      handleChange(e);

                      if (e.target.value.length < 8) {
                        return;
                      }

                      const isPwned = await usersService.isPwnedPassword(
                        e.target.value,
                      );
                      if (isPwned) {
                        setIsPasswordPwned(true);
                      } else {
                        setIsPasswordPwned(false);
                      }
                    }}
                    onBlur={handleBlur}
                    value={values.password}
                  />
                </div>
                {errors.password && touched.password && (
                  <p className="help is-danger">{errors.password}</p>
                )}
                <div className="field">
                  <label className="label">更新後のパスワード（確認）</label>
                  <input
                    type="password"
                    name="passwordConfirm"
                    className="input"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.passwordConfirm}
                  />
                </div>
                {errors.passwordConfirm && touched.passwordConfirm && (
                  <p className="help is-danger">{errors.passwordConfirm}</p>
                )}
                <div className={"has-text-centered"}>
                  <PrimaryButton
                    text={"更新する"}
                    disabled={isSubmitting || !isValid}
                    type="submit"
                  />
                </div>
                <Spinner loading={isSubmitting} />
              </form>
            )}
          </Formik>
        </div>
      </div>
    </LoginTemplate>
  );
});
